import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildRankIndex, rankIndex, countMatches, MAX_ROWS } from "../Util/rankOptions.js";
import { focusNextField } from "../Util/handleEnterKey.js";

/**
 * Combobox — the desktop keyboard dropdown behind DatalistSelect and
 * AccountPicker (Tally-style "List of Ledger Accounts" look).
 *
 * Presentational core: the wrapper owns the value; this owns the list (open
 * state, highlighted row, ranking, portal positioning) and navigation keys.
 *
 * Props
 *   text            string   – what the input shows (controlled)
 *   onTextChange    (text)   – user typed
 *   selectedKey     any      – key of the current pick (highlighted on open)
 *   options, getLabel, getKey, getCode?, getSub?, getWeight?, getBadge?, renderRight?
 *   onPick          (option) – commit it; Combobox then advances focus when the
 *                              pick came from Enter/click (see advanceAfterPick)
 *   onEnter         (ctx) → false to let Enter through (grid passthrough)
 *   onEscape        ()       – restore the value from before editing
 *   onBlur          (e, ctx) – ctx = { typed, active, matches }
 *   onExtraKey      (e, ctx) → true when handled (e.g. Alt+C)
 *   allowFree       boolean  – freeSolo: no auto-highlight unless exact match
 *   emptyHint       node     – shown when nothing matches
 *
 * Keys: ↑/↓ open + move · PgUp/PgDn · Enter picks the highlighted row ·
 * Tab picks it only while typing · Esc closes. Inside a grid
 * (input[data-grid-cell]) a closed list leaves ↑/↓ to the grid.
 *
 * keydown is bound NATIVELY on the input so it runs before the form's
 * Enter-advance and the grid listeners (see GOTCHAS.md, keyboard-first).
 * The list renders in a portal (position: fixed) so tables with overflow and
 * modals never clip it.
 */

let uid = 0;

export default function Combobox({
    id,
    listId: listIdProp,
    inputRef: inputRefProp,
    text,
    onTextChange,
    selectedKey = null,
    options = [],
    getLabel = (o) => o.label,
    getKey = (o) => o.id,
    getCode,
    getSub,
    getWeight,
    getBadge,
    renderRight,
    onPick,
    onEnter,
    onEscape,
    onBlur,
    onFocus,
    onExtraKey,
    onReactKeyDown,
    onWheel,
    allowFree = false,
    emptyHint = "No match",
    error = false,
    placeholder,
    disabled,
    required,
    autoFocus,
    className,
    style,
    inputProps = {},
}) {
    const ownRef = useRef(null);
    const inputRef = inputRefProp || ownRef;
    const listRef = useRef(null);
    const [autoId] = useState(() => `cbx-${++uid}`);
    const listId = listIdProp || autoId;
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState(false);
    const [active, setActive] = useState(0);
    const [rect, setRect] = useState(null);
    // How the list was opened: a click on an empty field highlights nothing
    // (Enter then just moves on); keyboard opens at the pick or row 0.
    const openSourceRef = useRef("key");
    // Last real pointer position over the list — Chrome fires synthetic mouse
    // events when rows move under a still cursor; those must not move the
    // highlight (Enter would pick the row under the mouse).
    const pointerRef = useRef(null);
    // Set while we hand focus back from the list, so wrappers' onFocus
    // (select-all, "value before editing") doesn't treat it as a new visit.
    const refocusRef = useRef(false);

    // Ranking work only while the list is live — closed pickers in long line
    // grids (often with inline-filtered `options`) cost nothing per render.
    const live = open || typed;
    const buildIndex = () => buildRankIndex(options, { getLabel, getCode, getSub, getWeight });
    // Accessors are usually inline lambdas; the options array is the real key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const index = useMemo(() => (live ? buildIndex() : null), [options, live]);
    const query = typed ? text : "";
    const isSelected = (o) => selectedKey != null && String(getKey(o)) === String(selectedKey);
    const matches = useMemo(
        () => (index ? rankIndex(index, query, MAX_ROWS, typed ? undefined : isSelected) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [index, query, typed, selectedKey]
    );
    const total = useMemo(
        () => (index && matches.length >= MAX_ROWS ? countMatches(index, query) : matches.length),
        [index, query, matches.length]
    );

    // Opening without typing: highlight the current pick (the anchored window
    // above guarantees it is in the list).
    useLayoutEffect(() => {
        if (!open) return;
        pointerRef.current = null;
        if (typed) return;
        const at = matches.findIndex(isSelected);
        setActive(at >= 0 ? at : openSourceRef.current === "click" ? -1 : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const openList = (source = "key") => {
        openSourceRef.current = source;
        setOpen(true);
    };
    const close = () => {
        setOpen(false);
        setTyped(false);
    };

    // Advance after the parent re-rendered — but only if focus is still here
    // and the input still shows the pick. A modal the pick opened keeps its
    // focus, and chip-adders (which clear the input) stay for the next entry.
    const advanceRef = useRef(0);
    const cancelAdvance = () => {
        if (advanceRef.current) cancelAnimationFrame(advanceRef.current);
        advanceRef.current = 0;
    };
    const advanceAfterPick = (opt) => {
        cancelAdvance();
        advanceRef.current = requestAnimationFrame(() => {
            advanceRef.current = 0;
            const el = inputRef.current;
            if (!el || document.activeElement !== el) return;
            // Parents may re-case the value (e.g. upper-case catalogue nos.).
            const norm = (v) => String(v ?? "").trim().toLowerCase();
            if (norm(el.value) !== norm(getLabel(opt))) return;
            focusNextField(el);
        });
    };

    const pick = (opt, advance) => {
        close();
        onPick?.(opt);
        if (advance) advanceAfterPick(opt);
    };

    // Keep the highlighted row in view.
    useEffect(() => {
        if (!open) return;
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
    }, [active, open]);

    // Re-measure the input; only a real change re-renders (no loop).
    const measure = () => {
        const r = inputRef.current?.getBoundingClientRect();
        if (!r) return;
        setRect((prev) =>
            prev && prev.top === r.top && prev.bottom === r.bottom && prev.left === r.left && prev.width === r.width
                ? prev
                : { top: r.top, bottom: r.bottom, left: r.left, width: r.width }
        );
    };

    // Follow layout shifts that aren't scrolls (banners, error lines, cards
    // collapsing) — cheap, and a no-op when nothing moved.
    useLayoutEffect(() => {
        if (open) measure();
    });

    // Place the portal list under (or above) the input and follow scrolling.
    useLayoutEffect(() => {
        if (!open) return undefined;
        const place = (e) => {
            // The list scrolling itself doesn't move the input. A resize
            // event's target is window, which isn't a Node.
            if (e?.target instanceof Node && listRef.current?.contains(e.target)) return;
            measure();
        };
        place();
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
        // measure() only reads refs and setRect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, inputRef]);

    const ctx = () => ({
        open,
        typed,
        // Closed pickers rank lazily, only when a handler asks.
        get matches() { return index ? matches : rankIndex(buildIndex(), query); },
        active: open && active >= 0 ? matches[active] || null : null,
        inGrid: !!inputRef.current?.dataset?.gridCell,
        openList,
        close,
    });

    const onKeyDown = (e) => {
        // Enter/arrows during IME composition belong to the IME.
        if (e.isComposing || e.keyCode === 229) return;
        const c = ctx();
        if (onExtraKey?.(e, c)) return;
        if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !open && c.inGrid) return;
        const last = matches.length - 1;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                if (!open) openList();
                else setActive((a) => Math.min(a + 1, last));
                break;
            case "ArrowUp":
                e.preventDefault();
                if (!open) openList();
                else setActive((a) => Math.max(a - 1, 0));
                break;
            case "PageDown":
                if (open) { e.preventDefault(); setActive((a) => Math.min(Math.max(a, 0) + 10, last)); }
                break;
            case "PageUp":
                if (open) { e.preventDefault(); setActive((a) => Math.max(a - 10, 0)); }
                break;
            case "Enter": {
                // Modified Enter belongs to page shortcuts.
                if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
                if (c.active) { e.preventDefault(); pick(c.active, true); return; }
                const res = onEnter?.(c);
                if (res === false) { setOpen(false); return; }
                e.preventDefault();
                break;
            }
            case "Escape":
                if (open || typed) {
                    // Claim Esc so the page doesn't treat it as "leave voucher".
                    e.preventDefault();
                    close();
                    onEscape?.();
                }
                break;
            case "Tab":
                if (open && typed && c.active) pick(c.active, false);
                else setOpen(false);
                break;
            default:
        }
    };

    const keyHandlerRef = useRef(onKeyDown);
    keyHandlerRef.current = onKeyDown;
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return undefined;
        const listener = (e) => keyHandlerRef.current(e);
        el.addEventListener("keydown", listener);
        return () => el.removeEventListener("keydown", listener);
    }, [inputRef]);

    const handleChange = (e) => {
        const val = e.target.value;
        setTyped(true);
        setOpen(true);
        onTextChange?.(val);
        // Blank text highlights nothing: Enter/Tab on a cleared field must
        // leave it blank, not fill in the first option.
        const q = val.trim().toLowerCase();
        if (!q) { setActive(-1); return; }
        // Highlight an exact label hit even if something else ranks first
        // (case-only duplicates, code matches). freeSolo highlights nothing
        // else, so Enter keeps the free text.
        const ranked = rankIndex(index || buildIndex(), val);
        const t = val.trim();
        let exact = ranked.findIndex((o) => String(getLabel(o)) === t);
        if (exact < 0) exact = ranked.findIndex((o) => String(getLabel(o)).toLowerCase() === q);
        setActive(allowFree ? exact : Math.max(0, exact));
    };

    const handleBlur = (e) => {
        // Focus went into the list (tablet tap, scrollbar drag): keep it open
        // and hand focus back so the pick can land.
        if (e.relatedTarget && listRef.current?.contains(e.relatedTarget)) {
            refocusRef.current = true;
            inputRef.current?.focus();
            return;
        }
        const c = ctx();
        setOpen(false);
        setTyped(false);
        onBlur?.(e, c);
    };

    // Flip above the input when the viewport has no room below.
    const LIST_MAX = 300;
    let listPos = null;
    if (rect) {
        const below = window.innerHeight - rect.bottom;
        const up = below < Math.min(LIST_MAX, 160) && rect.top > below;
        listPos = {
            left: Math.min(rect.left, Math.max(0, window.innerWidth - Math.max(rect.width, 280) - 4)),
            width: Math.max(rect.width, 280),
            maxHeight: Math.max(120, Math.min(LIST_MAX, (up ? rect.top : below) - 8)),
            ...(up ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }),
        };
    }

    const list = open && listPos && (
        <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            // Keep focus in the input while clicking a row, and keep portal
            // clicks from bubbling (React-tree) into the picker's ancestors.
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => e.stopPropagation()}
            onMouseMove={(e) => {
                const last = pointerRef.current;
                pointerRef.current = { x: e.clientX, y: e.clientY };
                if (!last || (last.x === e.clientX && last.y === e.clientY)) return;
                const row = e.target.closest?.("[data-idx]");
                if (row) setActive(Number(row.dataset.idx));
            }}
            className="ui-menu"
            style={{ position: "fixed", zIndex: 2000, ...listPos }}
        >
            {matches.length === 0 && <li className="ui-menu-note">{emptyHint}</li>}
            {matches.map((o, i) => {
                const sub = getSub?.(o);
                const badge = getBadge?.(o);
                return (
                    <li
                        key={`${String(getKey(o))}-${i}`}
                        id={`${listId}-${i}`}
                        data-idx={i}
                        role="option"
                        aria-selected={i === active}
                        onClick={() => pick(o, true)}
                        className="ui-menu-item"
                    >
                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {getLabel(o)}
                            {sub && <span className="ui-menu-sub">{sub}</span>}
                        </span>
                        {badge && (
                            <span className="ui-menu-badge">
                                {badge}
                            </span>
                        )}
                        {renderRight?.(o)}
                    </li>
                );
            })}
            {total > matches.length && (
                <li className="ui-menu-note">
                    +{total - matches.length} more — keep typing to narrow
                </li>
            )}
        </ul>
    );

    return (
        <>
            <input
                ref={inputRef}
                id={id}
                type="text"
                role="combobox"
                className={className}
                aria-expanded={open}
                aria-controls={listId}
                // Lets pages focus a picker by its listId (salesOrderHelpers.focusListInput).
                data-list-id={listId}
                aria-autocomplete="list"
                aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
                aria-invalid={error ? true : undefined}
                autoComplete="off"
                autoFocus={autoFocus}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                value={text ?? ""}
                onChange={handleChange}
                onFocus={(e) => {
                    if (refocusRef.current) { refocusRef.current = false; return; }
                    onFocus?.(e);
                }}
                onKeyDown={onReactKeyDown}
                onWheel={onWheel}
                onClick={() => {
                    // Clicked back into the field: drop a queued post-pick advance.
                    cancelAdvance();
                    if (open) setOpen(false);
                    else openList("click");
                }}
                onBlur={handleBlur}
                style={style}
                {...inputProps}
            />
            {list && createPortal(list, document.body)}
        </>
    );
}
